import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../Components/ui/button';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Enterprise marketing-site Header for SearchJob.ai.
 *
 * Behavior:
 *   • Transparent at top of page (lets the Hero breathe)
 *   • Frosted-glass with subtle border once the user scrolls past 24px
 *   • "Products" opens a FULL-WIDTH mega-menu (drops from the header,
 *     spans the container width) — two columns of real offerings +
 *     a promo card, styled in the SearchJob.ai theme.
 *
 * Buttons use the platform's primary brand color; no gradient buttons.
 */
export default function Header() {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Toggle frosted-glass treatment based on scroll position.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll while mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Close the Products mega-menu on Escape.
  useEffect(() => {
    if (!productsOpen) return;
    const onKey = (e) => e.key === 'Escape' && setProductsOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [productsOpen]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);
  const closeProducts = () => setProductsOpen(false);

  return (
    <>
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        scrolled || productsOpen || isMobileMenuOpen
          ? 'bg-background/95 backdrop-blur-xl border-b border-border shadow-sm'
          : 'bg-background/40 backdrop-blur-sm'
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* ─── LOGO (original CDN image, untouched) ─── */}
          <a
            href="/hirewithus"
            className="flex items-center shrink-0"
            aria-label="SearchJob.ai home"
          >
            <img
              src= "/Newzenalphablue.png"
              alt="SearchJob.ai"
              className="h-10 w-auto"
            />
          </a>

          {/* ─── DESKTOP NAV ─── */}
          <nav className="hidden md:flex items-center gap-1">
            {/* Products mega-menu trigger */}
            <button
              type="button"
              onClick={() => setProductsOpen((v) => !v)}
              aria-expanded={productsOpen}
              className={cn(
                'flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors outline-none',
                productsOpen
                  ? 'text-foreground bg-muted/60'
                  : 'text-foreground/80 hover:text-foreground hover:bg-muted/60'
              )}
            >
              Products
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 opacity-70 transition-transform duration-200',
                  productsOpen && 'rotate-180'
                )}
              />
            </button>

            <NavLink href="/hirewithus#features">Solutions</NavLink>
            <NavLink href="/blog">Resources</NavLink>
            <NavLink href="/hirewithus/about">Company</NavLink>
            <NavLink href="/hirewithus/contact">Contact</NavLink>
          </nav>

          {/* ─── DESKTOP CTAs ─── */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => navigate('/hirewithus/login')}
              className="text-sm font-medium text-foreground/80 hover:text-foreground px-3 py-1.5 rounded-md hover:bg-muted/60 transition-colors"
            >
              Sign in
            </button>
            <Button
              onClick={() => navigate('/hirewithus/signup')}
              variant="default"
              size="default"
              className="!bg-primary !text-primary-foreground hover:!bg-primary/90 font-semibold"
            >
              Request a Demo
            </Button>
          </div>

          {/* ─── MOBILE TOGGLE ─── */}
          <button
            className="md:hidden p-2 rounded-md text-foreground hover:bg-muted/60 transition-colors"
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            aria-label="Toggle navigation menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* ─── PRODUCTS MEGA-MENU (full-width, drops from header) ─── */}
      {productsOpen && (
        <>
          {/* click-catcher */}
          <div
            className="hidden md:block fixed inset-0 z-40 cursor-default"
            onClick={closeProducts}
            aria-hidden="true"
          />
          <div className="hidden md:block absolute left-0 right-0 top-16 z-50 bg-background border-t border-border shadow-2xl animate-mega overflow-hidden">
            {/* ── decorative background (behind content) ── */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-24 left-[8%] w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-mega-glow-1" />
              <div className="absolute -top-20 left-[45%] w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl animate-mega-glow-2" />
              <div className="absolute -bottom-24 right-[10%] w-[28rem] h-72 bg-teal-500/10 rounded-full blur-3xl animate-mega-glow-1" />
              {/* one-time light sweep as the menu opens */}
              <div className="absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-emerald-400/20 to-transparent animate-mega-sweep" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="grid grid-cols-[1.05fr_1.05fr_0.9fr] gap-10">
                {/* ── What we offer (platform) ── */}
                <div>
                  <MenuLabel>What we offer</MenuLabel>
                  <div className="mt-2 space-y-1">
                    <MegaItem
                      href="/hirewithus/ai-sourcing-screening"
                      title="AI Sourcing & Screening"
                      desc="Auto-source candidates, then score and rank every resume against your JD."
                      onClick={closeProducts}
                    />
                    <MegaItem
                      href="/hirewithus/ai-call-screening"
                      title="AI Call Screening"
                      desc="AI voice calls that screen candidates on JD role questions."
                      onClick={closeProducts}
                    />
                    <MegaItem
                      href="/hirewithus/coding-assessment"
                      title="Coding Assessment"
                      desc="Proctored coding tests with built-in cheating detection."
                      onClick={closeProducts}
                    />
                    <MegaItem
                      href="/hirewithus/screening-assessment"
                      title="Screening Assessment"
                      desc="Technical & role-based skills screening, pre-interview."
                      onClick={closeProducts}
                    />
                    <MegaItem
                      href="/hirewithus/functional-assessment"
                      title="Functional Assessment"
                      desc="Behavioural, personality & communication tests."
                      onClick={closeProducts}
                    />
                  </div>
                </div>

                {/* ── Staffing services ── */}
                <div className="border-l border-border/60 pl-10">
                  <MenuLabel>Staffing services</MenuLabel>
                  <div className="mt-2 space-y-1">
                    <MegaItem
                      href="/hirewithus/staffing"
                      title="Recruitment & Staffing"
                      desc="Full-service hiring, run by our recruiters + AI."
                      onClick={closeProducts}
                    />
                    <MegaItem
                      href="/hirewithus/staffing"
                      title="Permanent Recruitment"
                      desc="Full-time hires across every level, sourced & vetted."
                      onClick={closeProducts}
                    />
                    <MegaItem
                      href="/hirewithus/staffing"
                      title="Contract Staffing"
                      desc="Flexible contract & temp talent, payroll managed."
                      onClick={closeProducts}
                    />
                    <MegaItem
                      href="/hirewithus/fractional"
                      title="Fractional Hiring"
                      desc="Part-time CFOs, CMOs, CHROs & senior leaders, assessed & vetted."
                      onClick={closeProducts}
                    />
                    <MegaItem
                      href="/hirewithus/staffing"
                      title="AI-native RPO"
                      desc="Embed our recruiters + AI as your talent team."
                      onClick={closeProducts}
                    />
                    <MegaItem
                      href="/hirewithus/staffing"
                      title="Executive Search"
                      desc="Confidential leadership & niche senior hiring."
                      onClick={closeProducts}
                    />
                  </div>
                </div>

                {/* ── Promo card (botimg as the background) ── */}
                <div className="relative overflow-hidden rounded-2xl border border-primary/10 flex flex-col min-h-[18rem]">
                  {/* background image */}
                  <img
                    src="/botimg.png"
                    alt="SearchJob.ai AI agent presenting candidate shortlists"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                  {/* readability overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/55" />
                  {/* content overlaid */}
                  <div className="relative z-10 flex flex-col h-full p-5">
                    <h4 className="text-lg font-bold text-white leading-snug mb-1.5">
                      Watch the AI agents work
                    </h4>
                    <p className="text-sm text-white/85 leading-relaxed">
                      See sourcing, screening, interviews and offers run end to
                      end — live, no login required.
                    </p>
                    <a
                      href="/hirewithus/demo"
                      onClick={closeProducts}
                      className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-full bg-white text-primary text-sm font-semibold px-5 py-2.5 hover:bg-white/90 transition-colors shadow-lg"
                    >
                      Try it live
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes mobile-slide {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-mobile-slide { animation: mobile-slide 0.2s ease-out; }
        @keyframes mega-in {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-mega { animation: mega-in 0.18s ease-out; }

        /* Drifting brand glows behind the mega-menu */
        @keyframes mega-glow-1 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(24px, 12px); }
        }
        @keyframes mega-glow-2 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-24px, 16px); }
        }
        .animate-mega-glow-1 { animation: mega-glow-1 9s ease-in-out infinite; }
        .animate-mega-glow-2 { animation: mega-glow-2 11s ease-in-out infinite; }

        /* One-time light sweep that plays as the menu opens */
        @keyframes mega-sweep {
          0% { transform: translateX(0) skewX(-12deg); opacity: 0; }
          15% { opacity: 1; }
          100% { transform: translateX(650%) skewX(-12deg); opacity: 0; }
        }
        .animate-mega-sweep { animation: mega-sweep 1s ease-out; }

        @media (prefers-reduced-motion: reduce) {
          .animate-mega, .animate-mega-glow-1, .animate-mega-glow-2,
          .animate-mega-sweep, .animate-mobile-slide { animation: none; }
        }
      `}</style>
    </header>

      {/* ─── MOBILE OVERLAY MENU ─── */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-background z-40 animate-mobile-slide flex flex-col">
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
            <MobileSectionLabel>What we offer</MobileSectionLabel>
            <MobileLink href="/hirewithus/ai-sourcing-screening" onClick={closeMobileMenu}>
              AI Sourcing & Screening
            </MobileLink>
            <MobileLink href="/hirewithus/ai-call-screening" onClick={closeMobileMenu}>
              AI Call Screening
            </MobileLink>
            <MobileLink href="/hirewithus/coding-assessment" onClick={closeMobileMenu}>
              Coding Assessment
            </MobileLink>
            <MobileLink href="/hirewithus/screening-assessment" onClick={closeMobileMenu}>
              Screening Assessment
            </MobileLink>
            <MobileLink href="/hirewithus/functional-assessment" onClick={closeMobileMenu}>
              Functional Assessment
            </MobileLink>

            <MobileSectionLabel>Staffing services</MobileSectionLabel>
            <MobileLink href="/hirewithus/staffing" onClick={closeMobileMenu}>
              Recruitment & Staffing
            </MobileLink>
            <MobileLink href="/hirewithus/staffing" onClick={closeMobileMenu}>
              Permanent Recruitment
            </MobileLink>
            <MobileLink href="/hirewithus/staffing" onClick={closeMobileMenu}>
              Contract Staffing
            </MobileLink>
            <MobileLink href="/hirewithus/fractional" onClick={closeMobileMenu}>
              Fractional Hiring
            </MobileLink>
            <MobileLink href="/hirewithus/staffing" onClick={closeMobileMenu}>
              AI-native RPO
            </MobileLink>
            <MobileLink href="/hirewithus/staffing" onClick={closeMobileMenu}>
              Executive Search
            </MobileLink>

            <MobileSectionLabel>More</MobileSectionLabel>
            <MobileLink href="/hirewithus#features" onClick={closeMobileMenu}>
              Solutions
            </MobileLink>
            <MobileLink href="/blog" onClick={closeMobileMenu}>
              Resources
            </MobileLink>
            <MobileLink href="/hirewithus/about" onClick={closeMobileMenu}>
              Company
            </MobileLink>
            <MobileLink href="/hirewithus/contact" onClick={closeMobileMenu}>
              Contact
            </MobileLink>
          </div>

          {/* Pinned CTA footer — always reachable without scrolling */}
          <div className="shrink-0 border-t border-border bg-background px-4 py-4 space-y-2 shadow-[0_-10px_24px_-18px_rgba(0,0,0,0.35)]">
            <button
              onClick={() => {
                closeMobileMenu();
                navigate('/hirewithus/login');
              }}
              className="w-full py-2.5 rounded-md border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              Sign in
            </button>
            <Button
              onClick={() => {
                closeMobileMenu();
                navigate('/hirewithus/signup');
              }}
              className="w-full !bg-primary !text-primary-foreground hover:!bg-primary/90 font-semibold"
            >
              Request a Demo
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

function NavLink({ href, children }) {
  return (
    <a
      href={href}
      className="px-3 py-1.5 rounded-md text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-muted/60 transition-colors"
    >
      {children}
    </a>
  );
}

function MenuLabel({ children }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 px-3">
      {children}
    </p>
  );
}

function MegaItem({ href, title, desc, onClick }) {
  return (
    <a
      href={href}
      onClick={onClick}
      className="group block rounded-xl p-3 text-left hover:bg-primary/5 transition-colors"
    >
      <span className="block text-[15px] font-semibold text-foreground group-hover:text-primary transition-colors">
        {title}
      </span>
      <span className="block mt-0.5 text-[13px] text-muted-foreground leading-relaxed">
        {desc}
      </span>
    </a>
  );
}

function MobileSectionLabel({ children }) {
  return (
    <p className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
      {children}
    </p>
  );
}

function MobileLink({ href, children, onClick }) {
  return (
    <a
      href={href}
      onClick={onClick}
      className="block px-3 py-2 rounded-md text-[15px] font-medium text-foreground hover:bg-muted transition-colors"
    >
      {children}
    </a>
  );
}
